/**
 * calcPW — Picture Window (Fixed) cut list calculations
 *
 * All formulas verified line-by-line against PW DNA spreadsheet.
 * Excel cell references noted inline for auditing.
 *
 * WindowRow.jsx stores glass fields with prefix "Picture":
 *   win.PictureGlassPkg   win.PictureGlassOpts
 *   win.PictureGridType   win.PictureGridPat   win.PictureGridV   win.PictureGridH
 *
 * PW has ONE glass pane: "Picture Window Glass" — uses Picture prefix fields.
 *
 * Formula key (DNA sheet):
 *   B21  = indivW  (overall width ÷ numLites)
 *   C21  = indivH  (overall height)
 *   B19  = unitQty (qty × numLites)
 *   B12  = overallW (overall width, used for starter & drip cap length)
 *
 *   Glass:    qty=B19,    W=B21-4.625,  H=C21-4.625
 *   Frame W:  qty=B19*2,  length=B21+0.25,  part=5154-color
 *   Frame H:  qty=B19*2,  length=C21+0.25,  part=5154-color
 *   Glzbd W:  qty=B19*1,  length=B21-4.25,  part=5457-glazbeadColor
 *   Glzbd H:  qty=B19*1,  length=C21-5.375, part=5457-glazbeadColor
 *   Starter:  qty=without fin→B19, with fin→0; length=B12=overallW
 *   Drip Cap: qty=IF(single,0,IF(without fin,0,IF(with fin,D19*1=qty))); length=overallW
 *   Snap Mull:qty per config; length=C21-0.125=indivH-0.125
 */
export function calcPW(win) {
  const {
    // ── Dimensions ──────────────────────────────────────────────────────────
    width: overallW,
    height,
    // ── Identity ────────────────────────────────────────────────────────────
    config  = 'Single',
    color   = 'White',
    finType = 'Without Fin',
    qty     = 1,
    label   = '',
    // ── Gas ─────────────────────────────────────────────────────────────────
    gasFill = 'Argon',
    // ── Glass — PW uses Picture prefix (single pane, fixed) ─────────────────
    PictureGlassPkg  = 'DLA',
    PictureGlassOpts = 'None',
    PictureGridType  = 'None',
    PictureGridPat   = 'None',
    PictureGridV     = '0',
    PictureGridH     = '0',
  } = win;

  // ── Color helpers ─────────────────────────────────────────────────────────
  const colorLower = (color || '').toLowerCase();
  const isDark     = colorLower.includes('woodgrain');
  const withFin    = finType === 'With Fin' || finType === 'With Fin & J';

  // Woodgrain glazbead/part color logic (matches DNA IF chain exactly):
  // IF(color="dk woodgrain/white","White",IF(color="dk woodgrain/clay","Clay",color))
  const glazbeadColor = colorLower.includes('white') ? 'White'
                      : colorLower.includes('clay')  ? 'Clay'
                      : color;

  // ── Lite multiplier ───────────────────────────────────────────────────────
  // DNA: single=1, twin=2, triple=3, quad=4, quin=5, q/h/q=2
  const liteMap = {
    Single:   1,
    '2-Lite': 2,
    '3-Lite': 3,
    '4-Lite': 4,
    '5-Lite': 5,
    'Q/H/Q':  2,
    // Legacy names
    Twin: 2, Triple: 3, Quad: 4, Quin: 5,
  };
  const numLites = liteMap[config] || 1;
  const indivW   = overallW / numLites;   // B21 = B12 / numLites
  const indivH   = height;                // C21
  const unitQty  = qty * numLites;        // B19 = unit qty

  // ── Glass ─────────────────────────────────────────────────────────────────
  // DNA Glass row:
  //   Qty    = B19           (unitQty — one pane per lite, fixed window)
  //   Width  = B21-4.625     (indivW - 4.625)
  //   Height = C21-4.625     (indivH - 4.625)
  const glassW = indivW - 4.625;          // B21-4.625
  const glassH = indivH - 4.625;          // C21-4.625

  // ── Grid helper ───────────────────────────────────────────────────────────
  // Grid Length H = glassW - 1   (DNA: =L10-1)
  // Grid Length V = glassH - 1   (DNA: =L11-1)
  // Horizontals   = gridH × unitQty  (DNA: =M17*L9)
  // Verticals     = gridV × unitQty  (DNA: =L17*L9)
  // GridColor     = IF(Flat|Contour, color, "")  (DNA: =IF(L15="Flat",B10,...))
  const gridV    = parseInt(PictureGridV) || 0;
  const gridH    = parseInt(PictureGridH) || 0;
  const hasGrids = !!PictureGridType
                && PictureGridType !== 'None'
                && PictureGridType !== ''
                && (gridV > 0 || gridH > 0);

  const gridLenH  = hasGrids ? glassW - 1 : null;
  const gridLenV  = hasGrids ? glassH - 1 : null;
  const horizQty  = hasGrids ? gridH * unitQty : 0;   // DNA: =M17*L9
  const vertQty   = hasGrids ? gridV * unitQty : 0;   // DNA: =L17*L9
  // Grid color matches INSIDE-visible frame color.
  // Frame naming: "Inside/Outside" (e.g. "Dk Woodgrain/White" = dark inside, white outside).
  // Inside grid face matches inside frame color; outside grid face is always White.
  // We report the INSIDE color so Tracy Corners knows what to build.
  const insideGridColor = color.includes('/')
    ? color.split('/')[0].trim()
    : color;
  const gridColor = hasGrids ? insideGridColor : '';

  // ── Frame ─────────────────────────────────────────────────────────────────
  // PW Frame Widths:  qty=B19*2, length=B21+0.25, part=5154-color
  // PW Frame Heights: qty=B19*2, length=C21+0.25, part=5154-color
  const frameW    = indivW + 0.25;        // B21+0.25
  const frameH    = indivH + 0.25;        // C21+0.25
  const framePart = `5154-${color}`;

  // ── Glazbead ──────────────────────────────────────────────────────────────
  // (x2) Glazbead Widths:  qty=B19*1, length=B21-4.25,  part=5457-glazbeadColor
  // (x2) Glazbead Heights: qty=B19*1, length=C21-5.375, part=5457-glazbeadColor
  // DNA part: IF(color="dk woodgrain/white","5457-White",IF(..."5457-Clay","5457-color"))
  const glazbeadW    = indivW - 4.25;     // B21-4.25
  const glazbeadH    = indivH - 5.375;    // C21-5.375
  const glazbeadPart = `5457-${glazbeadColor}`;

  // ── Starter ───────────────────────────────────────────────────────────────
  // DNA: qty = IF(without fin, B19*1, IF(with fin, B19*0))
  //      length = B12 = overallW
  //      part: IF(woodgrain/white,"8351-White",IF(woodgrain/clay,"8351-Clay","8351-color"))
  const starterQty  = withFin ? 0 : unitQty;
  const starterPart = `8351-${glazbeadColor}`;

  // ── 5293 Drip Cap ─────────────────────────────────────────────────────────
  // DNA: qty = IF(single,0, IF(without fin,0, IF(with fin, D19*1)))
  //            D19 = qty (raw window qty, not unitQty)
  //      length = IF(single,"False", B19) → use overallW when not single
  //      part: IF(woodgrain/white,"5293-White",IF(...,"5293-Clay","5293-color"))
  //      Note: DNA uses B17 (color) not B10 for drip cap woodgrain check — same field
  const dripCapQty  = (numLites === 1 || !withFin) ? 0 : qty;
  const dripCapLen  = numLites === 1 ? null : overallW;
  const dripCapPart = `5293-${glazbeadColor}`;

  // ── (x2) Snap Mull ────────────────────────────────────────────────────────
  // DNA: qty = single→0, twin→D12*1=qty, triple→D12*2, quad→D12*3, quin→D12*4, q/h/q→D12*2
  //      length = C21-0.125 = indivH-0.125
  //      part = 8309-color
  const snapMullQtyMap = {
    Single:0, '2-Lite':1, '3-Lite':2, '4-Lite':3, '5-Lite':4, 'Q/H/Q':2,
    Twin:1, Triple:2, Quad:3, Quin:4,
  };
  const snapMullQty = (snapMullQtyMap[config] || 0) * qty;
  const snapMullLen = indivH - 0.125;     // C21-0.125

  // ── Return ────────────────────────────────────────────────────────────────
  return {
    label,
    type:      'PW',
    color,
    overallW,
    width:     overallW,
    height,
    config:    config || 'Single',
    qty,
    totalQty:  unitQty,
    indivW,
    finType,
    screenType:  null,
    flexScreen:  null,
    stdScreen:   null,

    // ── Glass — single fixed pane per lite ──────────────────────────────────
    // qty = unitQty (B19) — one pane per lite, not ×2 like HS/DH
    glass: [
      {
        label:       'Picture Window Glass',
        qty:         unitQty,               // B19*1
        width:       glassW,                // B21-4.625
        height:      glassH,                // C21-4.625
        glassType:   PictureGlassPkg,
        glassConfig: PictureGlassOpts !== 'None' ? PictureGlassOpts : '',
        gasFill,
        // Grid fields
        grids:       hasGrids,
        gridType:    hasGrids ? PictureGridType : '',
        gridPattern: hasGrids ? (PictureGridPat === 'None' ? '' : PictureGridPat) : '',
        gridQtyV:    hasGrids ? gridV : 0,
        gridQtyH:    hasGrids ? gridH : 0,
        gridColor:   hasGrids ? gridColor : '',
        gridLenH,
        gridLenV,
        horizQty,
        vertQty,
      },
    ],

    // ── Frame ───────────────────────────────────────────────────────────────
    frame: [
      { label: 'PW Frame Widths',  qty: unitQty * 2, length: frameW, part: framePart },  // B19*2
      { label: 'PW Frame Heights', qty: unitQty * 2, length: frameH, part: framePart },  // B19*2
    ],

    // ── Glazbead ────────────────────────────────────────────────────────────
    glazbead: [
      { label: '(x2) Glazbead Widths',  qty: unitQty, length: glazbeadW, part: glazbeadPart }, // B19*1
      { label: '(x2) Glazbead Heights', qty: unitQty, length: glazbeadH, part: glazbeadPart }, // B19*1
    ],

    // ── Misc ────────────────────────────────────────────────────────────────
    misc: [
      ...(starterQty > 0 ? [
        { label: 'Starter', qty: starterQty, length: overallW, part: starterPart },
      ] : []),
      ...(dripCapQty > 0 ? [
        { label: '5293 Drip Cap', qty: dripCapQty, length: dripCapLen, part: dripCapPart },
      ] : []),
      ...(snapMullQty > 0 ? [
        { label: '(x2) Snap Mull', qty: snapMullQty, length: snapMullLen, part: `8309-${color}` },
      ] : []),
    ],

    // ── Summary / reference sizes ────────────────────────────────────────────
    // FRAME SIZE row: B19=unitQty, B21=indivW, C21=indivH
    summary: {
      glassW,
      glassH,
    },
  };
}